import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { message } from 'antd';
import { useProjectStore } from '../../hooks/useProjectStore';
import { fractureAPI } from '../../services/api';
import type { TimeStep, StageResult } from './types';
import type { FracDesign, FracSimulation } from './types';
import DynamicSimulationCurves from './DynamicSimulationCurves';

// ═══════════════════════════════════════════════
// FracCurvesPage — 压裂数据曲线 (参照 FrSmart 商业化风格)
//   可拖拽段标签 + 复选框图例 + 悬停十字线 + 左/右双Y轴
// ═══════════════════════════════════════════════

const CS = {
  bg: '#FFFFFF', canvasBg: '#FDFDFE', grid: 'rgba(30,42,58,0.06)',
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#C2533A', accentLight: 'rgba(194,83,58,0.08)',
  headerBorder: '#D6DAE0', panelHeaderBg: '#F0F2F5',
  tooltipBg: 'rgba(30,42,58,0.88)', tooltipText: '#FFFFFF',
  crosshair: 'rgba(30,42,58,0.20)',
  fontUI: '"Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};

const COLORS = ['#C2533A', '#2874A6', '#1E8449', '#6A4C93', '#D4770C', '#0891B2', '#BE185D', '#4D7C0F', '#7C3AED', '#B45309'];

interface SeriesDef {
  key: string;
  label: string;
  color: string;
  axis: 'left' | 'right';
  unit: string;
  dash?: number[];
  get: (ts: TimeStep, stage?: StageResult) => number;
}

type SimBrief = { id: number; design: number; model_type: string; created_at: string; results?: StageResult[] };
type PumpScheduleStep = {
  step?: string;
  fluid_m3?: number;
  sand_conc?: number;
  sand_conc_kgm3?: number;
  rate?: number;
  rate_m3min?: number;
};
type DesignBrief = {
  id: number;
  name: string;
  is_active: boolean;
  pump_rate?: number;
  stages?: Array<{ stage_number: number; schedule?: PumpScheduleStep[] }>;
};

export default function FracCurvesPage() {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;
  const [curveDesigns, setCurveDesigns] = useState<FracDesign[]>([]);
  const [curveDesignId, setCurveDesignId] = useState<number | null>(null);
  const [curveSimulations, setCurveSimulations] = useState<FracSimulation[]>([]);
  const [curveSimulationId, setCurveSimulationId] = useState<number | null>(null);
  const [curveLoading, setCurveLoading] = useState(true);
  const unpack = <T,>(data: T[] | { results?: T[] }): T[] => Array.isArray(data) ? data : data?.results ?? [];

  useEffect(() => {
    if (!projectId) { setCurveLoading(false); return; }
    setCurveLoading(true);
    fractureAPI.listDesigns(projectId).then(({ data }) => {
      const rows = unpack<FracDesign>(data);
      setCurveDesigns(rows);
      setCurveDesignId(rows.find((design) => design.is_active)?.id ?? rows[0]?.id ?? null);
    }).catch((error) => message.error(error?.message || '压裂方案加载失败'));
  }, [projectId]);

  useEffect(() => {
    if (!curveDesignId) { setCurveSimulations([]); setCurveSimulationId(null); setCurveLoading(false); return; }
    setCurveLoading(true);
    fractureAPI.listSimulations({ design: curveDesignId }).then(({ data }) => {
      const rows = unpack<FracSimulation>(data).sort((left, right) => (
        new Date(right.created_at).getTime() - new Date(left.created_at).getTime()
      ));
      setCurveSimulations(rows);
      setCurveSimulationId(rows[0]?.id ?? null);
    }).catch((error) => message.error(error?.message || '模拟结果加载失败'))
      .finally(() => setCurveLoading(false));
  }, [curveDesignId]);

  const curveSimulation = useMemo(
    () => curveSimulations.find((item) => item.id === curveSimulationId) ?? null,
    [curveSimulations, curveSimulationId],
  );
  if (!projectId) return <div style={{ padding: 40, color: CS.textSec }}>请先选择项目</div>;
  return <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: CS.bg }}>
    <div style={{ padding: '7px 18px', borderBottom: `1px solid ${CS.headerBorder}`, display: 'flex', alignItems: 'center', gap: 10 }}>
      <h2 style={{ margin: 0, fontSize: 15, color: CS.text }}>动态曲线</h2>
      <select value={curveDesignId ?? ''} onChange={(event) => setCurveDesignId(Number(event.target.value))} style={{ fontSize: 11, padding: '4px 7px', border: `1px solid ${CS.headerBorder}`, borderRadius: 3 }}>
        {curveDesigns.map((design) => <option key={design.id} value={design.id}>{design.is_active ? '★ ' : ''}{design.name}</option>)}
      </select>
      <select value={curveSimulationId ?? ''} onChange={(event) => setCurveSimulationId(Number(event.target.value))} style={{ fontSize: 11, padding: '4px 7px', border: `1px solid ${CS.headerBorder}`, borderRadius: 3 }}>
        {curveSimulations.map((item) => <option key={item.id} value={item.id}>#{item.id} · {new Date(item.created_at).toLocaleString('zh-CN')}</option>)}
      </select>
      <span style={{ fontSize: 10, color: CS.textMuted }}>{curveLoading ? '加载中…' : curveSimulation ? `${curveSimulation.model_type} · ${curveSimulation.results.length} 段` : '没有可用模拟结果'}</span>
    </div>
    <div style={{ flex: 1, minHeight: 0 }}>
      {curveSimulation
        ? <DynamicSimulationCurves stages={curveSimulation.results ?? []} />
        : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: CS.textMuted }}>请先运行一次真实压裂模拟。</div>}
    </div>
  </div>;
}

function LegacyFracCurvesPage() {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;
  const [designs, setDesigns] = useState<DesignBrief[]>([]);
  const [designId, setDesignId] = useState<number | null>(null);
  const [sims, setSims] = useState<SimBrief[]>([]);
  const [simId, setSimId] = useState<number | null>(null);
  const [stages, setStages] = useState<StageResult[]>([]);
  const [selStage, setSelStage] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const un = (d: any) => (Array.isArray(d) ? d : d?.results ?? []);

  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    fractureAPI.listDesigns(projectId).then(({ data }) => {
      const ds: DesignBrief[] = un(data); setDesigns(ds);
      const act = ds.find(d => d.is_active) ?? ds[0];
      if (act) setDesignId(act.id); else setLoading(false);
    }).catch(() => setLoading(false));
  }, [projectId]);

  useEffect(() => {
    if (!designId) return; setLoading(true);
    fractureAPI.listSimulations({ design: designId }).then(({ data }) => {
      const ss: SimBrief[] = un(data); setSims(ss);
      if (ss.length) {
        const latest = ss.reduce((a, b) => new Date(a.created_at).getTime() > new Date(b.created_at).getTime() ? a : b);
        setSimId(latest.id);
        const rs = (latest.results ?? []).sort((a, b) => a.stage_number - b.stage_number);
        setStages(rs); if (rs.length) setSelStage(rs[0].stage_number);
      } else { setSimId(null); setStages([]); setSelStage(null); }
    }).catch((e) => message.error(e?.message || '加载失败')).finally(() => setLoading(false));
  }, [designId]);

  const stage = useMemo(() => stages.find(s => s.stage_number === selStage) ?? null, [stages, selStage]);
  const selectedDesign = useMemo(() => designs.find((design) => design.id === designId) ?? null, [designs, designId]);
  const designStage = useMemo(
    () => selectedDesign?.stages?.find((item) => item.stage_number === selStage) ?? null,
    [selectedDesign, selStage],
  );
  const timeSteps: TimeStep[] = stage?.time_steps ?? [];

  if (!projectId) return <div style={{ padding: 40, color: CS.textSec }}>请先选择项目</div>;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: CS.bg, fontFamily: 'inherit' }}>
      <div style={{ padding: '6px 20px', borderBottom: `1px solid ${CS.headerBorder}`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: CS.text, margin: 0 }}>动态曲线</h2>
        <select value={designId ?? ''} onChange={e => setDesignId(Number(e.target.value))}
          style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4, border: `1px solid ${CS.headerBorder}` }}>
          {designs.length === 0 && <option>无方案</option>}
          {designs.map(d => <option key={d.id} value={d.id}>{d.is_active ? '★ ' : ''}{d.name}</option>)}
        </select>
        {sims.length > 0 && (
          <select value={simId ?? ''} onChange={e => {
            const sid = Number(e.target.value); setSimId(sid);
            const s = sims.find(x => x.id === sid);
            const rs = (s?.results ?? []).sort((a, b) => a.stage_number - b.stage_number);
            setStages(rs); setSelStage(rs[0]?.stage_number ?? null);
          }} style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4, border: `1px solid ${CS.headerBorder}` }}>
            {sims.map(s => <option key={s.id} value={s.id}>#{s.id} · {new Date(s.created_at).toLocaleString('zh-CN')}</option>)}
          </select>
        )}
        <span style={{ fontSize: 12, color: CS.textMuted }}>|</span>
        <select value={selStage ?? ''} onChange={e => setSelStage(Number(e.target.value))}
          style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4, border: `1px solid ${CS.headerBorder}` }}>
          {stages.map(s => <option key={s.stage_number} value={s.stage_number}>第{s.stage_number}段</option>)}
        </select>
        <span style={{ fontSize: 11, color: CS.textMuted }}>
          {loading ? '加载中…' : (stage ? `半缝长 ${stage.half_length}m · 缝高 ${stage.height}m · 液效 ${stage.efficiency_pct}%` : '')}
        </span>
      </div>
      {!stage ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: CS.textMuted, fontSize: 13 }}>
          {loading ? '加载中…' : '暂无模拟结果 — 请先到「裂缝扩展模拟」运行模型'}
        </div>
      ) : (
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 1, background: CS.headerBorder, minHeight: 0 }}>
          <PumpProgramPanel timeSteps={timeSteps} schedule={designStage?.schedule ?? []} fallbackRate={selectedDesign?.pump_rate} />
          <PressurePanel timeSteps={timeSteps} stage={stage} />
          <DimensionPanel timeSteps={timeSteps} />
          <AreaVolumePanel timeSteps={timeSteps} stage={stage} />
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════
// InteractiveChart — 带复选框+悬停十字线的通用 Canvas 图表
// ═══════════════════════════════════════════════

function InteractiveChart({ title, series, timeSteps, stage }: {
  title: string; series: SeriesDef[]; timeSteps: TimeStep[]; stage?: StageResult;
}) {
  const [visible, setVisible] = useState<Record<string, boolean>>(() => {
    const v: Record<string, boolean> = {}; series.forEach(s => { v[s.key] = true; }); return v;
  });
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const toggle = (k: string) => setVisible(p => ({ ...p, [k]: !p[k] }));

  const draw = useCallback(() => {
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 2;
    const rect = cv.getBoundingClientRect();
    if (rect.width < 10 || rect.height < 10) return;
    cv.width = rect.width * dpr; cv.height = rect.height * dpr; ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    ctx.fillStyle = CS.canvasBg; ctx.fillRect(0, 0, W, H);

    if (!timeSteps.length) {
      ctx.fillStyle = CS.textMuted; ctx.font = `12px ${CS.fontUI}`; ctx.textAlign = 'center';
      ctx.fillText('无扩展历程数据', W / 2, H / 2); return;
    }
    const pad = { L: 50, R: 50, T: 8, B: 30 };
    const pw = W - pad.L - pad.R, ph = H - pad.T - pad.B;
    const ts = timeSteps.map(s => s.t_s);
    const tMin = ts[0], tMax = ts[ts.length - 1] || 1;
    const xOf = (t: number) => pad.L + ((t - tMin) / (tMax - tMin || 1)) * pw;

    let lMin = Infinity, lMax = -Infinity, rMin = Infinity, rMax = -Infinity;
    const vis = series.filter(s => visible[s.key]);
    for (const s of vis) {
      const vals = timeSteps.map(t => s.get(t, stage));
      const mn = Math.min(...vals), mx = Math.max(...vals);
      if (s.axis === 'left') { lMin = Math.min(lMin, mn); lMax = Math.max(lMax, mx); }
      else { rMin = Math.min(rMin, mn); rMax = Math.max(rMax, mx); }
    }
    if (!isFinite(lMin)) { lMin = 0; lMax = 1; }
    if (!isFinite(rMin)) { rMin = 0; rMax = 1; }
    const lRng = lMax - lMin || 1, rRng = rMax - rMin || 1;

    // Grid
    ctx.strokeStyle = CS.grid; ctx.lineWidth = 0.5;
    for (let i = 0; i <= 4; i++) {
      const y = pad.T + (i / 4) * ph; ctx.beginPath(); ctx.moveTo(pad.L, y); ctx.lineTo(W - pad.R, y); ctx.stroke();
    }
    for (let i = 0; i <= 5; i++) {
      const x = pad.L + (i / 5) * pw; ctx.beginPath(); ctx.moveTo(x, pad.T); ctx.lineTo(x, H - pad.B); ctx.stroke();
    }
    // Curves
    for (const s of vis) {
      const vals = timeSteps.map(t => s.get(t, stage));
      const rng = s.axis === 'left' ? lRng : rRng, min = s.axis === 'left' ? lMin : rMin;
      ctx.strokeStyle = s.color; ctx.lineWidth = 2; ctx.setLineDash(s.dash ?? []); ctx.beginPath();
      timeSteps.forEach((step, i) => {
        const x = xOf(step.t_s), y = pad.T + ph - ((vals[i] - min) / rng) * ph;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.stroke(); ctx.setLineDash([]);
    }
    // Y ticks
    ctx.fillStyle = CS.textMuted; ctx.font = `9px ${CS.fontMono}`; ctx.textAlign = 'right';
    for (let i = 0; i <= 4; i++) {
      const v = lMin + ((4 - i) / 4) * lRng;
      ctx.fillText(v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2), pad.L - 6, pad.T + (i / 4) * ph + 3);
    }
    ctx.textAlign = 'left';
    for (let i = 0; i <= 4; i++) {
      const v = rMin + ((4 - i) / 4) * rRng;
      ctx.fillText(v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2), W - pad.R + 6, pad.T + (i / 4) * ph + 3);
    }
    // X ticks
    ctx.fillStyle = CS.textMuted; ctx.font = `9px ${CS.fontMono}`; ctx.textAlign = 'center';
    for (let i = 0; i <= 6; i++) {
      const t = tMin + (i / 6) * (tMax - tMin);
      ctx.fillText(`${(t / 60).toFixed(1)}`, xOf(t), H - pad.B + 14);
    }
    ctx.fillStyle = CS.textSec; ctx.font = `10px ${CS.fontUI}`; ctx.fillText('时间 (min)', pad.L + pw / 2, H - 4);

    // Crosshair + Tooltip
    if (hoverIdx != null && hoverIdx >= 0 && hoverIdx < timeSteps.length) {
      const cur = timeSteps[hoverIdx]; const cx = xOf(cur.t_s);
      ctx.strokeStyle = CS.crosshair; ctx.lineWidth = 1; ctx.setLineDash([3, 3]);
      ctx.beginPath(); ctx.moveTo(cx, pad.T); ctx.lineTo(cx, H - pad.B); ctx.stroke(); ctx.setLineDash([]);
      const tipW = 172, tipH = 16 + vis.length * 16;
      let tipX = cx + 12; if (tipX + tipW > W - 8) tipX = cx - tipW - 12;
      const tipY = pad.T + 2;
      ctx.fillStyle = CS.tooltipBg; ctx.beginPath();
      if (ctx.roundRect) { ctx.roundRect(tipX, tipY, tipW, tipH, 4); ctx.fill(); }
      else { ctx.fillRect(tipX, tipY, tipW, tipH); }
      ctx.fillStyle = CS.tooltipText; ctx.font = `bold 10px ${CS.fontMono}`; ctx.textAlign = 'left';
      ctx.fillText(`时间: ${(cur.t_s / 60).toFixed(2)} min`, tipX + 8, tipY + 14);
      vis.forEach((s, si) => {
        ctx.fillStyle = s.color; ctx.font = `10px ${CS.fontMono}`;
        ctx.fillText(`${s.label}: ${s.get(cur, stage).toFixed(2)} ${s.unit}`, tipX + 8, tipY + 30 + si * 16);
      });
      // Dots
      vis.forEach(s => {
        const vals = timeSteps.map(t => s.get(t, stage));
        const rng = s.axis === 'left' ? lRng : rRng, min = s.axis === 'left' ? lMin : rMin;
        const py = pad.T + ph - ((vals[hoverIdx] - min) / rng) * ph;
        ctx.fillStyle = s.color; ctx.beginPath(); ctx.arc(cx, py, 3.5, 0, 2 * Math.PI); ctx.fill();
      });
    }
  }, [timeSteps, stage, series, visible, hoverIdx]);

  useEffect(() => { draw(); }, [draw]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    const cv = canvasRef.current; if (!cv || !timeSteps.length) return;
    const rect = cv.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const frac = (mx - 50) / Math.max(1, rect.width - 100);
    setHoverIdx(Math.max(0, Math.min(timeSteps.length - 1, Math.round(frac * (timeSteps.length - 1)))));
  }, [timeSteps]);
  const onMouseLeave = useCallback(() => setHoverIdx(null), []);

  const leftS = series.filter(s => s.axis === 'left');
  const rightS = series.filter(s => s.axis === 'right');

  return (
    <div style={{ background: CS.canvasBg, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
      <div style={{ padding: '3px 10px', fontSize: 11, fontWeight: 600, color: CS.accent, background: CS.panelHeaderBg, borderBottom: `1px solid ${CS.headerBorder}`, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span>{title}</span>
        {leftS.map(s => (
          <label key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, cursor: 'pointer', fontSize: 10, fontWeight: 400, color: visible[s.key] ? CS.textSec : CS.textMuted, userSelect: 'none' }}>
            <input type="checkbox" checked={visible[s.key] ?? true} onChange={() => toggle(s.key)} style={{ width: 12, height: 12, accentColor: s.color, cursor: 'pointer', margin: 0 }} />
            <span style={{ width: 10, height: 2, background: visible[s.key] ? s.color : CS.textMuted, borderRadius: 1, display: 'inline-block' }} />
            {s.label}
          </label>
        ))}
        <span style={{ flex: 1 }} />
        {rightS.map(s => (
          <label key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, cursor: 'pointer', fontSize: 10, fontWeight: 400, color: visible[s.key] ? CS.textSec : CS.textMuted, userSelect: 'none' }}>
            <input type="checkbox" checked={visible[s.key] ?? true} onChange={() => toggle(s.key)} style={{ width: 12, height: 12, accentColor: s.color, cursor: 'pointer', margin: 0 }} />
            <span style={{ width: 10, height: 2, background: visible[s.key] ? s.color : CS.textMuted, borderRadius: 1, display: 'inline-block' }} />
            {s.label}
          </label>
        ))}
      </div>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <canvas ref={canvasRef} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave} style={{ width: '100%', height: '100%', display: 'block', cursor: 'crosshair' }} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
// 四个面板 — 各自定义曲线系列
// ═══════════════════════════════════════════════

const finiteNumber = (...values: unknown[]): number | undefined => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const scheduleAtTime = (
  schedule: PumpScheduleStep[],
  timeS: number,
  fallbackRate?: number,
): { rateM3min: number; concentrationKgm3: number } => {
  let elapsedS = 0;
  let last = { rateM3min: Math.max(0, finiteNumber(fallbackRate) ?? 0), concentrationKgm3: 0 };
  for (const row of schedule) {
    const rateM3min = Math.max(0, finiteNumber(row.rate, row.rate_m3min, fallbackRate) ?? 0);
    const concentrationKgm3 = Math.max(0, finiteNumber(row.sand_conc, row.sand_conc_kgm3) ?? 0);
    const fluidM3 = Math.max(0, finiteNumber(row.fluid_m3) ?? 0);
    const durationS = rateM3min > 0 ? fluidM3 / (rateM3min / 60) : 0;
    last = { rateM3min, concentrationKgm3 };
    if (durationS > 0 && timeS <= elapsedS + durationS) return last;
    elapsedS += durationS;
  }
  return last;
};

function PumpProgramPanel({ timeSteps, schedule, fallbackRate }: {
  timeSteps: TimeStep[];
  schedule: PumpScheduleStep[];
  fallbackRate?: number;
}) {
  const series: SeriesDef[] = [
    {
      key: 'rate', label: '施工排量', color: COLORS[1], axis: 'left', unit: 'm³/min',
      get: (step) => {
        const solverRate = finiteNumber(step.flow_rate_m3s, step.flow_rate);
        return solverRate !== undefined && solverRate > 0
          ? solverRate * 60
          : scheduleAtTime(schedule, step.t_s, fallbackRate).rateM3min;
      },
    },
    {
      key: 'proppant', label: '支撑剂浓度', color: COLORS[2], axis: 'right', unit: 'kg/m³', dash: [4, 3],
      get: (step) => finiteNumber(step.proppant_concentration_kgm3, step.sand_conc_kgm3)
        ?? scheduleAtTime(schedule, step.t_s, fallbackRate).concentrationKgm3,
    },
  ];
  return <InteractiveChart title="泵注程序" series={series} timeSteps={timeSteps} />;
}

function PressurePanel({ timeSteps, stage }: { timeSteps: TimeStep[]; stage: StageResult }) {
  const baseStress = finiteNumber(stage.stress_layers?.sigma_m_mpa, stage.clusters?.[0]?.base_shmin_mpa) ?? 0;
  const series: SeriesDef[] = [
    {
      key: 'fracture_pressure', label: '裂缝压力', color: COLORS[0], axis: 'left', unit: 'MPa',
      get: (step) => finiteNumber(step.fracture_pressure_mpa) ?? baseStress + (finiteNumber(step.net_pressure_mpa) ?? 0),
    },
    {
      key: 'net_pressure', label: '净压力', color: COLORS[3], axis: 'right', unit: 'MPa', dash: [5, 3],
      get: (step) => finiteNumber(step.net_pressure_mpa) ?? 0,
    },
  ];
  return <InteractiveChart title="压力响应" series={series} timeSteps={timeSteps} stage={stage} />;
}

function DimensionPanel({ timeSteps }: { timeSteps: TimeStep[] }) {
  const series: SeriesDef[] = [
    { key: 'height', label: '缝高', color: COLORS[1], axis: 'left', unit: 'm', get: s => s.h_m ?? 0 },
    { key: 'length', label: '裂缝长度', color: COLORS[0], axis: 'left', unit: 'm', get: s => s.L_m },
    { key: 'width', label: '入口隙宽', color: COLORS[3], axis: 'right', unit: 'mm', dash: [4, 3], get: s => s.w0_mm },
  ];
  return <InteractiveChart title="裂缝尺寸" series={series} timeSteps={timeSteps} />;
}

function AreaVolumePanel({ timeSteps, stage }: { timeSteps: TimeStep[]; stage: StageResult }) {
  const hC = stage?.height || 1;
  const series: SeriesDef[] = [
    { key: 'area', label: '裂缝面积', color: COLORS[0], axis: 'left', unit: 'm²', get: s => s.L_m * (s.h_m ?? hC) },
    { key: 'volume', label: '裂缝体积', color: COLORS[1], axis: 'left', unit: 'm³', dash: [5, 3], get: s => s.Vf_m3 ?? (s.L_m * (s.h_m ?? hC) * (s.w0_mm / 1000) * 0.5) },
    { key: 'injected', label: '注入体积', color: COLORS[2], axis: 'left', unit: 'm³', dash: [2, 4], get: s => s.Vi_m3 ?? 0 },
    { key: 'efficiency', label: '液体效率', color: COLORS[4], axis: 'right', unit: '%', dash: [3, 3], get: s => s.eff_pct },
  ];
  return <InteractiveChart title="裂缝面积与体积" series={series} timeSteps={timeSteps} stage={stage} />;
}
